import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('ch_token'));
  const [loading, setLoading] = useState(true);

  const axiosAuth = useCallback(() => {
    const instance = axios.create({ baseURL: API, withCredentials: true });
    if (token) {
      instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    return instance;
  }, [token]);

  const checkAuth = useCallback(async () => {
    // Try JWT token first
    if (token) {
      try {
        const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        setUser(res.data);
        setLoading(false);
        return;
      } catch {
        localStorage.removeItem('ch_token');
        setToken(null);
      }
    }
    // Try session cookie (Google OAuth)
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
      setLoading(false);
      return;
    } catch {
      // Not authenticated
    }
    setUser(null);
    setLoading(false);
  }, [token]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('ch_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, username, role = 'subscriber') => {
    const res = await axios.post(`${API}/auth/register`, { email, password, username, role });
    localStorage.setItem('ch_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const loginWithGoogle = async (sessionId) => {
    const res = await axios.post(`${API}/auth/google/session`, { session_id: sessionId }, { withCredentials: true });
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem('ch_token');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (data) => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await axios.put(`${API}/auth/profile`, data, { headers, withCredentials: true });
    setUser(res.data);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, loginWithGoogle, logout, updateProfile, axiosAuth, API, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
